import classNames from 'classnames';
import React from 'react';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import SVGIcon from '../../components/SVGIcon';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import { PostType, TopicPrivacy } from '../../graphql/generated/graphql';
import getExternalBrandLogoProps from '../../integrations/getExternalBrandLogoProps';
import { UserRole } from '../../topic/topicPolicyCheck';
import addTestId from '../../utils/addTestId';
import { getStateIcon } from '../PostIcon';
import PostLayout from '../PostLayout';
import {
  PostPolicyAction,
  isAllowedThroughLinkAccess,
} from '../postPolicyCheck';
import * as styles from './PostStateBar.module.scss';
import PostStateBarActions from './PostStateBarActions';
import PostStateBarLabel from './PostStateBarLabel';
import PostStateBarTooltip from './PostStateBarTooltip';

const POST_STATE_BAR_BASE_FRAGMENT = gql(`
  fragment getPostStateBarData_post on Post {
    id
    archivedAt
    publishedAt
    type
    externalPost {
      id
      platform
    }
    topics {
      id
      privacy
    }
    ...isAllowedThroughLinkAccess_post
    ...PostStateBarLabel_post
  }
`);

const POST_STATE_BAR_FRAGMENT = gql(`
  fragment PostStateBar_post on Post {
    id
    ...getPostStateBarData_post
    ...PostStateBarActions_post
    ...PostStateBarTooltip_post
  }
`);

const POST_PREVIEW_STATE_BAR_FRAGMENT = gql(`
  fragment PostDetailPreviewStateBar_post on Post {
    id
    ...getPostStateBarData_post
  }
`);

const getPostStateBarData = (
  post: DocumentType<typeof POST_STATE_BAR_BASE_FRAGMENT>,
  options: { iconSize: number },
) => {
  const isPublic = isAllowedThroughLinkAccess(
    PostPolicyAction.ViewPost,
    post,
    UserRole.Anonymous,
  );
  const isPublishedInPublicTopic =
    !!post.publishedAt &&
    !post.archivedAt &&
    post.topics?.some((topic) => topic.privacy === TopicPrivacy.Public);

  const className: string | null = (() => {
    if (post.archivedAt) return styles.isArchived;
    if (!post.publishedAt) return styles.isDraft;
    if (post.type === PostType.Post && isPublic) return styles.isPublished;
    if (isPublishedInPublicTopic && !isPublic) return styles.isInPublicTopic;
    if (post.externalPost) return styles.isPublished;
    if (post.type === PostType.Template) return styles.isTemplate;
    if (post.type === PostType.Variant) return styles.isVariant;
    return null;
  })();

  const icon = post.externalPost ? (
    <ExternalBrandLogo
      {...getExternalBrandLogoProps(post.externalPost.platform)}
      height={options.iconSize}
    />
  ) : (
    <SVGIcon size={options.iconSize}>
      {getStateIcon({
        public: isPublic,
        type: post.type,
        draft: !post.publishedAt,
        archived: !!post.archivedAt,
      })}
    </SVGIcon>
  );

  const label = (
    <PostStateBarLabel
      post={post}
      isPublic={isPublic}
      isPublishedInPublicTopic={isPublishedInPublicTopic}
    />
  );

  return {
    isVisible: !!post && !!className,
    isPublic,
    icon,
    label,
    className,
  };
};

interface PostStateBarProps {
  post: DocumentType<typeof POST_STATE_BAR_FRAGMENT>;
}

const PostStateBar = ({ post }: PostStateBarProps) => {
  const [iconElement, setIconElement] = React.useState<HTMLElement | null>(
    null,
  );

  const { isVisible, icon, className, label, isPublic } = getPostStateBarData(
    post,
    { iconSize: 16 },
  );

  if (!isVisible) {
    return null;
  }

  return (
    <PostLayout.StateBar
      data-no-print
      {...addTestId('postStateBar')}
      className={classNames(styles.container, className)}
    >
      <div className={styles.container}>
        <PostStateBarTooltip anchorElement={iconElement} post={post}>
          <div className={styles.content}>
            <span ref={setIconElement}>{icon}</span>
            {label}
          </div>
        </PostStateBarTooltip>
        <PostStateBarActions post={post} isPublic={isPublic} />
      </div>
    </PostLayout.StateBar>
  );
};

interface PostDetailPreviewStateBarProps {
  post: DocumentType<typeof POST_PREVIEW_STATE_BAR_FRAGMENT>;
  contentMaxWidth?: number;
  className?: string;
}

export const PostDetailPreviewStateBar = ({
  post,
  contentMaxWidth,
  className: classNameProp,
}: PostDetailPreviewStateBarProps) => {
  const { isVisible, icon, label, className } = getPostStateBarData(post, {
    iconSize: 14,
  });

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={classNames(styles.previewContainer, className, classNameProp)}
    >
      <div
        className={styles.previewContent}
        style={{ maxWidth: contentMaxWidth }}
      >
        {icon}
        {label}
      </div>
    </div>
  );
};

export default PostStateBar;
